/* HOLY KICKS — Diwali festive layer behaviour.
   Self-contained: touches only .dc-diwali* nodes, adds no globals.
   Every countdown runs to the single real campaign end date supplied by
   snippets/dripclub-diwali.liquid, so it can never be reset per visitor. */
(() => {
  const DISMISS_KEY = 'dc-diwali-bar-dismissed';
  const pad = (value) => String(value).padStart(2, '0');

  /* ── Daily dispatch countdown ───────────────────────────────────
     Counts down to the next dispatch cutoff in India Standard Time, so
     every visitor sees the same deadline wherever they are, and it rolls
     over to a fresh 24 hours at the cutoff hour each night. The deadline
     is a real one: orders placed before it go out on the same working
     day, which is why the clock can reset without the claim becoming
     untrue. */

  const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

  const nextCutoff = (cutoffHour) => {
    // Shift into IST so the UTC getters/setters read as India wall-clock time.
    const istNow = new Date(Date.now() + IST_OFFSET_MS);
    const istCutoff = new Date(istNow);
    istCutoff.setUTCHours(cutoffHour, 0, 0, 0);

    // Today's cutoff already passed, so aim at tomorrow's.
    if (istCutoff <= istNow) istCutoff.setUTCDate(istCutoff.getUTCDate() + 1);

    return istCutoff.getTime() - IST_OFFSET_MS;
  };

  const collectClocks = () => {
    const clocks = [];

    document.querySelectorAll('[data-dc-daily-cutoff]').forEach((root) => {
      const cutoffHour = Number(root.dataset.dcDailyCutoff);
      if (!Number.isInteger(cutoffHour) || cutoffHour < 0 || cutoffHour > 23) return;

      root.querySelectorAll('[data-dc-countdown]').forEach((clock) => {
        const hours = clock.querySelector('[data-dc-hh]');
        const minutes = clock.querySelector('[data-dc-mm]');
        const seconds = clock.querySelector('[data-dc-ss]');
        if (!hours || !minutes || !seconds) return;

        clocks.push({ cutoffHour, hours, minutes, seconds });
      });
    });

    return clocks;
  };

  const startCountdown = () => {
    const clocks = collectClocks();
    if (!clocks.length) return;

    const tick = () => {
      const now = Date.now();

      clocks.forEach((clock) => {
        // Recomputed every tick, so the clock rolls straight into the next
        // day's window at the cutoff instead of sitting at zero.
        const totalSeconds = Math.max(0, Math.floor((nextCutoff(clock.cutoffHour) - now) / 1000));

        clock.hours.textContent = pad(Math.floor(totalSeconds / 3600));
        clock.minutes.textContent = pad(Math.floor((totalSeconds % 3600) / 60));
        clock.seconds.textContent = pad(totalSeconds % 60);
      });

      window.setTimeout(tick, 1000);
    };

    tick();
  };

  /* ── Dismissible festive bar ───────────────────────────────────── */

  const initializeBar = () => {
    const bar = document.querySelector('[data-dc-diwali-bar]');
    if (!bar) return;

    let dismissed = false;
    try {
      dismissed = window.sessionStorage.getItem(DISMISS_KEY) === 'true';
    } catch (error) {
      dismissed = false;
    }

    if (dismissed) {
      bar.hidden = true;
      return;
    }

    bar.querySelector('[data-dc-diwali-dismiss]')?.addEventListener('click', () => {
      bar.hidden = true;
      try {
        window.sessionStorage.setItem(DISMISS_KEY, 'true');
      } catch (error) {
        /* Private browsing — the bar simply returns on the next page. */
      }

      // The bar sits inside #header-group, whose measured height drives sticky
      // offsets. Nudge the theme's observers so they pick up the new height.
      window.dispatchEvent(new Event('resize'));
    });
  };

  /* ── Festive fact ticker ───────────────────────────────────────── */

  const initializeTicker = () => {
    const ticker = document.querySelector('[data-dc-diwali-ticker]');
    if (!ticker) return;

    const slides = Array.from(ticker.querySelectorAll('.dc-diwali-ticker__slide'));
    if (slides.length < 2) {
      if (slides.length === 1) ticker.classList.add('is-visible');
      return;
    }

    window.setTimeout(() => ticker.classList.add('is-visible'), 2200);

    let index = 0;
    window.setInterval(() => {
      slides[index].classList.remove('is-active');
      index = (index + 1) % slides.length;
      slides[index].classList.add('is-active');
    }, 4200);
  };

  const initialize = () => {
    startCountdown();
    initializeBar();
    initializeTicker();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
